/**
 * siblingFan — SIBLING BRANCHES MUST BE SEPARABLE WHERE THEY LEAVE.
 *
 * THE RULE: two flow edges leaving the SAME node must never draw as the same
 * line, and each one's label chip must ride its OWN line. So a source with more
 * than one outgoing branch on the SAME HANDLE does not send them all out of that
 * handle's single point — each branch gets its own PORT, spread along the
 * handle's edge of the node, ordered by where it is going.
 *
 * THE BUG THIS FIXES: a smoothstep path is a pure function of its two endpoints.
 * Every branch used to leave its source at the identical (sourceX, sourceY), in
 * the identical direction, so two branches whose targets share a column (a
 * `bottom` exit) or a row (a `right` exit) ran on top of each other for their
 * whole initial segment. One line drew exactly over the other, one chip landed
 * on the other, and the canvas denied that the fork existed — a conditional
 * short-circuit rendered as a straight pipeline. (Same defect, same rule, on the
 * self-host canvas; the two UIs share ZERO code, so each carries its own copy of
 * the rule and its own tripwire — `selfhosted/__tests__/ui-graph-sibling-fan`
 * there, `siblingFan.test.js` here.)
 *
 * THE FAN IS SIZED BY THE DEFICIT, NEVER BY THE ROOM AVAILABLE. A fork whose
 * branches already land in different columns/rows is short of nothing, comes out
 * with a pitch of ZERO, and is left EXACTLY as it was — no field stamped, the
 * same edge object, byte-identical rendering. Every number below is measured off
 * a stylesheet or the layouter's own node footprint; not one is picked.
 *
 * WHAT HAS NO TWIN HERE: the self-host rule's second half anchors each chip to
 * the OUTER side of its own line once the pills still touch. React Flow owns the
 * label point on this canvas (the path midpoint), and source-anchoring the chip
 * was tried and REJECTED here — it stacked the chips. So the cloud half is the
 * PORT fan only; see ReadonlyLabeledEdge's header.
 */
import { MEMBER_NODE_PREFIX } from './memberSatellites';
import { MODEL_NODE_PREFIX } from './modelSatellites';

// The two axes a port can slide along. A handle on the node's BOTTOM (or TOP)
// sends its line away vertically, so its ports spread horizontally, and the
// other way round for a LEFT/RIGHT handle.
export const AXIS_X = 'x';
export const AXIS_Y = 'y';

/**
 * Which axis a source handle's ports spread along — null for a handle this
 * layouter never assigns as a source (nothing to fan).
 *
 * The handle NAMES are the ones `layout.js` stamps ('bottom', 'source-top',
 * 'right'); the generic React Flow spellings are accepted so a hand-assigned
 * graph is not silently skipped.
 */
export function fanAxisOf(sourceHandle) {
  switch (sourceHandle) {
    case 'bottom':
    case 'top':
    case 'source-top':
    case 'source-bottom':
      return AXIS_X;
    case 'left':
    case 'right':
    case 'source-left':
    case 'source-right':
      return AXIS_Y;
    default:
      return null;
  }
}

// ── THE LABEL CHIP, measured off ReadonlyLabeledEdge.css ────────────────────
// Its BORDER BOX height: the text line, plus the block padding, plus the border
// on both sides. This is the one length that answers "how far apart must two
// lines be before each can carry its own chip", which is exactly what a fork
// that reads as one line is short of. A tripwire asserts each number against
// the stylesheet it was read from.
export const CHIP_FONT_SIZE = 11;    // font-size: 11px
export const CHIP_LINE_HEIGHT = 1.3; // line-height: 1.3
export const CHIP_PAD_Y = 2;         // padding: 2px 7px
export const CHIP_BORDER = 1;        // border: 1px solid …
export const CHIP_H =
  CHIP_FONT_SIZE * CHIP_LINE_HEIGHT + 2 * CHIP_PAD_Y + 2 * CHIP_BORDER;

// ── THE NODE'S OWN EDGE, measured off WorkflowNode.css / DecisionNode.css ───
// A port must stay ON the node's edge — no gap between the box and the line it
// emits — so it may only slide along the FLAT part of that edge, which stops
// where the corner rounds. The rendered box is the ICON BOX (both node kinds
// carry the identical one); `nodeSize()`'s 96 is ELK's spacing FOOTPRINT, which
// is deliberately wider than the box and is not an edge anything is drawn on.
export const ICON_BOX = 88;          // .gwe-*-node__icon-box width/height
export const ICON_BOX_RADIUS = 16;   // …its border-radius

/**
 * How far a port may sit from the centre of its handle's edge, in px.
 *
 * @param {{width:number,height:number}} size  the layouter's `nodeSize(node)`
 * @param {string} axis                        AXIS_X | AXIS_Y
 * @param {boolean} isTerminal                 a START/END pill rather than a box
 */
export function portHalfSpan(size, axis, isTerminal) {
  if (isTerminal) {
    // A terminal is a PILL (border-radius: 999px), so its corner radius is half
    // its height and the flat run is what is left of the extent along the axis.
    const r = (size?.height || 0) / 2;
    const extent = axis === AXIS_X ? (size?.width || 0) : (size?.height || 0);
    return Math.max(0, (extent - 2 * r) / 2);
  }
  return Math.max(0, (ICON_BOX - 2 * ICON_BOX_RADIUS) / 2);
}

/**
 * An ATTACHMENT hangs OFF a node; a BRANCH is the flow leaving it.
 *
 * Model discs and member satellites (consumed `uses` and started `starts`
 * alike) are attachments — drawn dashed, saying "this is attached to that",
 * never "the flow continues here". They must stay on the handle's centre while
 * the branches fan around them, exactly as on self-host. Each producer's own
 * structural marker is read here (never a slug, a label or a style), and a
 * tripwire asserts every edge those two producers emit is classified.
 */
export function isAttachmentEdge(edge) {
  if (!edge) return false;
  if (edge.targetHandle === 'disc') return true;
  const t = String(edge.target || '');
  return t.startsWith(MEMBER_NODE_PREFIX)
    || t.startsWith(MODEL_NODE_PREFIX);
}

/**
 * How far apart consecutive ports must sit, given the branches' own geometry.
 *
 * `sibs` are ALREADY ordered by the caller so the ports never cross — it owns
 * that, because only it knows the routing. Each is `{ lead, labeled }`:
 *   lead     the coordinate, on the fan axis, at which this branch finally
 *            parts from its siblings — its target's centre. Two branches whose
 *            leads already differ have separated on their own.
 *   labeled  whether this branch carries a chip.
 *
 * @param {Array<{lead:number,labeled:boolean}>} sibs
 * @param {number} halfMax  how far a port may sit from centre (portHalfSpan)
 * @returns {{pitch:number}} the gap between consecutive ports; 0 = leave alone
 */
export function planSiblingFan(sibs, halfMax) {
  const k = Array.isArray(sibs) ? sibs.length : 0;
  if (k < 2) return { pitch: 0 };
  // THE DEFICIT: how much port pitch each adjacent pair is SHORT of reading as
  // two lines each carrying its own chip. A pair already clear asks for nothing,
  // and a pair where neither branch carries a chip asks for nothing either —
  // there is no label to strand, and the self-host rule draws the same line.
  //
  // Ports sit at centre + (i - (k-1)/2)·pitch, so ADJACENT ports are exactly
  // `pitch` apart: what the pair is owed is what it is short of, undivided.
  let need = 0;
  for (let j = 1; j < k; j += 1) {
    const a = sibs[j - 1];
    const b = sibs[j];
    if (!a.labeled && !b.labeled) continue;
    const already = (b.lead || 0) - (a.lead || 0);
    const shortBy = CHIP_H - Math.abs(already);
    if (shortBy > need) need = shortBy;
  }
  if (!(need > 0)) return { pitch: 0 };
  return { pitch: Math.max(0, Math.min(need, (halfMax * 2) / (k - 1))) };
}

// ── ONE FIELD, ONE NAME ─────────────────────────────────────────────────────
// The port offset is RESOLVED in the layouter and merely APPLIED by the edge
// renderer — there is no second copy of the arithmetic to drift. Both sides go
// through these two functions rather than spelling the key, so the pair that
// must agree is a single declaration.
const FAN_KEY = 'sourceFan';
const NO_FAN = { dx: 0, dy: 0 };

/** Stamp a resolved port offset onto an edge, preserving whatever `data` holds. */
export function withSourceFan(edge, dx, dy) {
  if (!dx && !dy) return edge;
  return { ...edge, data: { ...(edge.data || {}), [FAN_KEY]: { dx, dy } } };
}

/** Read it back on the render side. Never null — an unfanned edge reads zeros. */
export function sourceFanOf(data) {
  const fan = data && data[FAN_KEY];
  if (!fan) return NO_FAN;
  return { dx: Number(fan.dx) || 0, dy: Number(fan.dy) || 0 };
}
